import { useAuth0 } from "@auth0/auth0-react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { exchangeSessionFromAuth0 } from "./api/client";

export type AuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Exchange Auth0 tokens for API cookie; no-op when not using Auth0. */
  ensureApiSession: () => Promise<void>;
};

const defaultAuth: AuthContextValue = {
  isAuthenticated: false,
  isLoading: false,
  ensureApiSession: async () => {},
};

const AuthContext = createContext<AuthContextValue>(defaultAuth);

/** Use under `Auth0Provider` (see main.tsx). */
export function AuthContextProvider({ children }: { children: ReactNode }) {
  const auth0 = useAuth0();
  const ensureApiSession = useCallback(async () => {
    if (!auth0.isAuthenticated) return;
    await exchangeSessionFromAuth0({
      getAccessTokenSilently: auth0.getAccessTokenSilently,
      getIdTokenClaims: auth0.getIdTokenClaims,
    });
  }, [
    auth0.isAuthenticated,
    auth0.getAccessTokenSilently,
    auth0.getIdTokenClaims,
  ]);

  const value = useMemo(
    (): AuthContextValue => ({
      isAuthenticated: auth0.isAuthenticated,
      isLoading: auth0.isLoading,
      ensureApiSession,
    }),
    [
      auth0.isAuthenticated,
      auth0.isLoading,
      ensureApiSession,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAppAuth(): AuthContextValue {
  return useContext(AuthContext);
}
